import { useState, useEffect } from 'react';
import { Phone, Globe, AlertCircle, CheckCircle } from 'lucide-react';
import { parsePhoneNumber, isValidPhoneNumber, getCountries, getCountryCallingCode, CountryCode } from 'libphonenumber-js';

// Popular countries to show first
const POPULAR_COUNTRIES: CountryCode[] = ['US', 'GB', 'CA', 'AU', 'IL', 'IN', 'DE', 'FR', 'ES', 'IT'];

interface PhoneNumberInputProps {
  value: string;
  onChange: (phoneNumber: string, isValid: boolean) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  autoDetectCountry?: boolean;
}

export function PhoneNumberInput({
  value,
  onChange,
  label = 'Phone Number',
  placeholder = '123456789',
  helperText,
  required = false,
  autoDetectCountry = true,
}: PhoneNumberInputProps) {
  const [countryCode, setCountryCode] = useState<CountryCode>('US');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  // Detect user's country on mount
  useEffect(() => {
    if (autoDetectCountry) {
      detectUserCountry();
    }
  }, [autoDetectCountry]);

  // Parse initial value if provided
  useEffect(() => {
    if (value && value.startsWith('+')) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed) {
          setCountryCode(parsed.country || 'US');
          setPhoneInput(parsed.nationalNumber);
        }
      } catch (error) {
        // If parsing fails, just use the value as-is
        setPhoneInput(value.replace(/^\+\d+/, ''));
      }
    }
  }, []);

  // Detect country from timezone or use IP geolocation
  const detectUserCountry = async () => {
    try {
      // Try to detect from timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const countryFromTimezone = getCountryFromTimezone(timezone);
      
      if (countryFromTimezone) {
        setCountryCode(countryFromTimezone);
        return;
      }

      // Fallback: Try IP geolocation
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.country_code) {
        setCountryCode(data.country_code as CountryCode);
      }
    } catch (error) {
      console.log('Could not detect country, using default (US)');
    }
  };

  // Simple timezone to country mapping
  const getCountryFromTimezone = (timezone: string): CountryCode | null => {
    const timezoneMap: Record<string, CountryCode> = {
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Asia/Jerusalem': 'IL',
      'Asia/Kolkata': 'IN',
      'Australia/Sydney': 'AU',
      'America/Toronto': 'CA',
    };
    return timezoneMap[timezone] || null;
  };

  // Validate phone number as user types
  useEffect(() => {
    if (!phoneInput.trim()) {
      setPhoneError('');
      setIsPhoneValid(false);
      onChange('', false);
      return;
    }

    try {
      const fullNumber = `+${getCountryCallingCode(countryCode)}${phoneInput}`;
      const isValid = isValidPhoneNumber(fullNumber, countryCode);
      
      if (isValid) {
        setPhoneError('');
        setIsPhoneValid(true);
        
        // Format the number using libphonenumber-js
        const phoneNumber = parsePhoneNumber(fullNumber, countryCode);
        onChange(phoneNumber.format('E.164'), true);
      } else {
        setPhoneError('Invalid phone number format');
        setIsPhoneValid(false);
        onChange(fullNumber, false);
      }
    } catch (error) {
      setPhoneError('Invalid phone number');
      setIsPhoneValid(false);
      onChange(`+${getCountryCallingCode(countryCode)}${phoneInput}`, false);
    }
  }, [phoneInput, countryCode, onChange]);

  const getFormattedPhone = () => {
    if (!isPhoneValid) return '';
    try {
      const fullNumber = `+${getCountryCallingCode(countryCode)}${phoneInput}`;
      const phoneNumber = parsePhoneNumber(fullNumber, countryCode);
      return phoneNumber.formatInternational();
    } catch {
      return '';
    }
  };

  // Get all countries sorted (popular first, then alphabetical)
  const allCountries = getCountries();
  const sortedCountries = [
    ...POPULAR_COUNTRIES,
    ...allCountries.filter(c => !POPULAR_COUNTRIES.includes(c)).sort(),
  ];

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
          <Globe className="inline h-4 w-4 mr-1" />
          Country
        </label>
        <select
          id="country"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value as CountryCode)}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
        >
          {sortedCountries.map((country, index) => {
            const isPopular = POPULAR_COUNTRIES.includes(country);
            return (
              <option key={country} value={country}>
                {isPopular && index < POPULAR_COUNTRIES.length ? '⭐ ' : ''}
                {country} (+{getCountryCallingCode(country)})
              </option>
            );
          })}
        </select>
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Phone className="h-5 w-5 text-gray-400" />
          </div>
          <div className="absolute inset-y-0 left-10 pl-3 flex items-center pointer-events-none border-r border-gray-300 pr-3">
            <span className="text-gray-500 text-sm">+{getCountryCallingCode(countryCode)}</span>
          </div>
          <input
            id="phone"
            name="phone"
            type="tel"
            required={required}
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, ''))}
            className={`block w-full pl-24 pr-10 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 ${
              phoneError && phoneInput.trim() 
                ? 'border-red-300 focus:border-red-500' 
                : isPhoneValid 
                ? 'border-green-300 focus:border-green-500' 
                : 'border-gray-300 focus:border-primary-500'
            }`}
            placeholder={placeholder}
          />
          {phoneInput.trim() && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {isPhoneValid ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
            </div>
          )}
        </div>
        
        {phoneInput.trim() && (
          <div className="mt-2">
            {phoneError ? (
              <p className="text-xs text-red-600 flex items-center">
                <AlertCircle className="h-3 w-3 mr-1" />
                {phoneError}
              </p>
            ) : isPhoneValid ? (
              <p className="text-xs text-green-600 flex items-center">
                <CheckCircle className="h-3 w-3 mr-1" />
                Valid: {getFormattedPhone()}
              </p>
            ) : null}
          </div>
        )}
        
        {helperText && (
          <p className="mt-2 text-xs text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    </div>
  );
}

